import { useQuery } from '@apollo/client';
import { useParams } from 'react-router';

import {
  type UserDetails,
  type HirerUserQuery,
  type HirerUserQueryVariables,
  UserStatus,
  HirerUserStatus,
} from 'src/graphql/generated.ts';
import { HIRER_USER_QUERY } from 'src/graphql/gql_queries';
import type { UseQueryResponse } from 'src/types/types';

export const useHirerAccountUser = (): UseQueryResponse<
  UserDetails | undefined
> => {
  const { id } = useParams();

  const variables = {
    input: { hirerUserId: String(id) },
  };

  const { loading, error, data } = useQuery<
    HirerUserQuery,
    HirerUserQueryVariables
  >(HIRER_USER_QUERY, {
    skip: !id,
    variables,
  });

  const user = mapUserDetails(data);
  return { loading, error, data: user };
};

const mapUserDetails = (
  data: HirerUserQuery | undefined,
): UserDetails | undefined => {
  if (!data) {
    return undefined;
  }

  const { hirerUser } = data as HirerUserQuery;

  return {
    id: hirerUser!.hirerUserId,
    firstName: hirerUser!.givenName,
    lastName: hirerUser!.familyName,
    status: hirerUser!.status
      ? mapStatus(hirerUser!.status)
      : UserStatus.PENDING,
    email: hirerUser!.email,
    permissions: hirerUser!.permissions,
  };
};

const mapStatus = (status: HirerUserStatus): UserStatus => {
  switch (status) {
    case HirerUserStatus.ACTIVE:
      return UserStatus.ACTIVE;
    case HirerUserStatus.DEACTIVATED:
      return UserStatus.DEACTIVATED;
    case HirerUserStatus.PENDING:
      return UserStatus.PENDING;
    default:
      return UserStatus.PENDING;
  }
};
